import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useNetwork } from 'wagmi'

import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import Connector from 'containers/Connector'
import { useAutoConnect } from 'hooks/useAutoConnect'
import useClickOutside from 'hooks/useClickOutside'
import WatchOrConnectWallet, {
	WatcherModeIcon,
} from 'sections/futures/WatcherMode/WatchOrConnectWallet'
import { selectSmartMarginAccount } from 'state/futures/smartMargin/selectors'
import { useAppSelector } from 'state/hooks'
import { selectIsWatcherMode } from 'state/wallet/selectors'
import media from 'styles/media'

import BalanceActions from './BalanceActions'
import ConnectionDot from './ConnectionDot'
import NetworksSwitcher from './NetworksSwitcher'
import OneClickTradingActions from './OneClickTradingActions'
import PreferencesPanel from './PreferencesPanel'
import WalletActions from './WalletActions'

const WalletButtons: React.FC = () => {
	const { t } = useTranslation()
	const { isWalletConnected } = Connector.useContainer()
	const { chain: network } = useNetwork()
	const smartMarginAccount = useAppSelector(selectSmartMarginAccount)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)

	const { openConnectModal } = useConnectModal()
	const [openConnectionDropdown, setOpenConnectionDropdown] = useState(false)
	const { ref } = useClickOutside(() => setOpenConnectionDropdown(false))

	const { openChainModal } = useChainModal()

	const handleOpenConnectModal = useCallback(() => {
		setOpenConnectionDropdown(false)
		openConnectModal?.()
	}, [openConnectModal])

	useAutoConnect()
	const WalletIsNotConnected = useCallback(
		() => (
			<>
				<Button
					size="small"
					variant="flat"
					noOutline
					onClick={handleOpenConnectModal}
					data-testid="connect-wallet"
					mono
				>
					<ConnectionDot />
					{t('common.wallet.connect')}
				</Button>
				<Button
					size="small"
					variant="flat"
					noOutline
					onClick={() => setOpenConnectionDropdown((x) => !x)}
					mono
				>
					<WatcherModeIcon />
					{t('common.wallet.watch')}
				</Button>
			</>
		),
		[handleOpenConnectModal, t]
	)

	const WalletIsConnectedButNotSupported = useCallback(
		() => (
			<>
				<Button size="small" mono variant="flat" onClick={openChainModal}>
					{t('homepage.l2.cta-buttons.switch-networks')}
				</Button>
				<Button size="small" variant="flat" data-testid="unsupported-network" mono>
					<ConnectionDot />
					{t('common.wallet.unsupported-network')}
				</Button>
			</>
		),
		[t, openChainModal]
	)

	const WalletIsConnectedAndSupported = useCallback(
		() => (
			<>
				{!!smartMarginAccount && <BalanceActions />}
				<WalletActions />
				{!isWatcherMode && (
					<>
						<NetworksSwitcher />
						{!!smartMarginAccount && <OneClickTradingActions />}
					</>
				)}
			</>
		),
		[smartMarginAccount, isWatcherMode]
	)

	return (
		<Container ref={ref}>
			{isWalletConnected ? (
				network?.unsupported ? (
					<WalletIsConnectedButNotSupported />
				) : (
					<WalletIsConnectedAndSupported />
				)
			) : (
				<WalletIsNotConnected />
			)}
			{openConnectionDropdown && (
				<WatchOrConnectWalletContainer>
					<WatchOrConnectWallet onDismiss={() => setOpenConnectionDropdown(false)} />
				</WatchOrConnectWalletContainer>
			)}
			<PreferencesPanel />
		</Container>
	)
}

const Container = styled.div`
	display: grid;
	grid-gap: 15px;
	grid-auto-flow: column;
`

const WatchOrConnectWalletContainer = styled(BaseCard)`
	z-index: 100;
	position: fixed;
	top: 15px;
	right: 70px;
	display: flex;
	flex-direction: column;
	width: 300px;
	row-gap: 20px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

export default WalletButtons
